if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) {
        this.removeListSemantic();
        this.initGalleryArrows();
      }
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) {
        activeMedia = this.getFirstNavigableMedia();
      }
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ activeMedia.dataset.mediaId }"]`);
      if (!activeThumbnail) return;
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    getNavigableItems() {
      const items = Array.from(this.elements.thumbnails.querySelectorAll('[id^="Slide-"]'));
      return items.filter((item) => {
        const target = item?.dataset?.target;
        if (!target) return false;
        const media = this.elements.viewer.querySelector(`[data-media-id="${ target }"]`);
        if (!media) return false;

        // Skip variant media that theme CSS keeps hidden (except the first one).
        if (media.classList.contains('product__media-item--variant') && !media.matches(':first-child')) return false;

        // Skip gang media when not currently active.
        if (media.hasAttribute('data-gang-option') && !media.classList.contains('gang__active')) return false;

        return true;
      });
    }

    getFirstNavigableMedia() {
      const items = this.getNavigableItems();
      if (!items.length) return null;
      const target = items[0]?.dataset?.target;
      if (!target) return null;
      return this.elements.viewer.querySelector(`[data-media-id="${ target }"]`);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    initGalleryArrows() {
      const prevBtn = this.elements.viewer.querySelector('button[name="previous"]');
      const nextBtn = this.elements.viewer.querySelector('button[name="next"]');
      if (prevBtn) prevBtn.addEventListener('click', this.onGalleryPrev.bind(this));
      if (nextBtn) nextBtn.addEventListener('click', this.onGalleryNext.bind(this));
    }

    onGalleryPrev(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const items = this.getNavigableItems();
      if (!items.length) return;
      const activeIndex = items.findIndex(el => el.classList.contains('is-active'));
      const activeMediaId = this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
      const mediaIndex = activeMediaId ? items.findIndex(el => el.dataset.target === activeMediaId) : -1;
      const safeActiveIndex = activeIndex >= 0 ? activeIndex : (mediaIndex >= 0 ? mediaIndex : 0);
      const prevIndex = safeActiveIndex > 0 ? safeActiveIndex - 1 : items.length - 1;
      const target = items[prevIndex]?.dataset.target;
      if (target) this.setActiveMedia(target, false);
    }

    onGalleryNext(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const items = this.getNavigableItems();
      if (!items.length) return;
      const activeIndex = items.findIndex(el => el.classList.contains('is-active'));
      const activeMediaId = this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
      const mediaIndex = activeMediaId ? items.findIndex(el => el.dataset.target === activeMediaId) : -1;
      const safeActiveIndex = activeIndex >= 0 ? activeIndex : (mediaIndex >= 0 ? mediaIndex : 0);
      const nextIndex = safeActiveIndex < items.length - 1 ? safeActiveIndex + 1 : 0;
      const target = items[nextIndex]?.dataset.target;
      if (target) this.setActiveMedia(target, false);
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
